import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Camera, Edit3 } from 'lucide-react';
import { useProfile, useUpdateProfile, useUploadAvatar, useTimezones } from '@/hooks/useProfile';
import { useToast } from '@/components/ui/use-toast';
import { useVersionTheme } from '@/hooks/useVersionTheme';
import i18n from '@/i18n/config';

const ProfilePage: React.FC = () => {
  const { toast } = useToast();
  const theme = useVersionTheme();

  // API hooks
  const { data: profile, isLoading, error } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const { data: timezones, isLoading: timezonesLoading } = useTimezones();

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('');
  const [language, setLanguage] = useState('en');
  const [company, setCompany] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  // Sync form data with profile from API
  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setEmail(profile.email || '');
      setPhone(profile.phone || '');
      setTimezone(profile.timezone || 'UTC');
      setLanguage(profile.language || 'en');
      setCompany('');
    }
  }, [profile]);

  // Create preview URL for selected file
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    // Change language immediately in i18next (instant feedback)
    i18n.changeLanguage(newLanguage);
  };

  const handleSave = useCallback(() => {
    const payload = {
      name,
      phone,
      language,
      timezone,
    };

    updateProfileMutation.mutate(payload, {
      onSuccess: () => {
        setIsEditing(false);
        toast({
          title: 'Profile updated successfully',
          variant: 'default',
        });
      },
      onError: (error: any) => {
        // Revert language if save fails
        i18n.changeLanguage(profile?.language || 'en');

        const errorMessage =
          error?.response?.data?.message || 'Failed to save your changes. Please try again.';

        toast({
          title: 'Error updating profile',
          description: errorMessage,
          variant: 'destructive',
        });
      },
    });
  }, [name, phone, language, timezone, updateProfileMutation, toast, profile]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    // Reset form data to original profile data
    if (profile) {
      setName(profile.name || '');
      setPhone(profile.phone || '');
      setLanguage(profile.language || 'en');
      setTimezone(profile.timezone || 'UTC');
    }
  }, [profile]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a valid image file',
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(file);

    uploadAvatarMutation.mutate(file, {
      onSuccess: () => {
        setSelectedFile(null);
        setImageError(false);
        toast({
          title: 'Avatar updated successfully',
          variant: 'default',
        });
      },
      onError: () => {
        setSelectedFile(null);
        toast({
          title: 'Error uploading avatar',
          description: 'Failed to upload your avatar. Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className={theme.textSecondary}>Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <svg
              className="h-16 w-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-red-400 font-semibold mb-2">Error loading profile</p>
          <p className={`${theme.textSecondary} text-sm`}>Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  // If no profile data, show empty state
  if (!profile) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className="text-center">
          <p className={theme.textSecondary}>No profile data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.textPrimary} p-6`}>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>Profile</h1>
            <p className={`${theme.textSecondary} mt-2`}>
              Manage your account information and view activity
            </p>
          </div>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition"
          >
            <Edit3 className="h-4 w-4" />
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>

        {/* Basic Info */}
        <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6`}>
          <div className="flex items-center gap-6 mb-6">
            <div className="relative">
              {previewUrl || (profile.avatarUrl && !imageError) ? (
                <img
                  src={
                    previewUrl || (profile.avatarUrl ? decodeURIComponent(profile.avatarUrl) : '')
                  }
                  alt="Profile avatar"
                  className={`h-20 w-20 rounded-full object-cover border-2 ${theme.border}`}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-2xl font-bold text-white">
                  {name
                    .split(' ')
                    .map(n => n.charAt(0))
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}
              {uploadAvatarMutation.isPending && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
                id="avatar-upload"
                disabled={uploadAvatarMutation.isPending}
              />
              {isEditing && (
                <label
                  htmlFor="avatar-upload"
                  className={`absolute -bottom-1 -right-1 p-1.5 ${theme.surface} rounded-full hover:${theme.surfaceHover} transition cursor-pointer ${uploadAvatarMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Camera className={`h-4 w-4 ${theme.textSecondary}`} />
                </label>
              )}
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>{name}</h2>
              {company && <p className={theme.textSecondary}>Account: {company}</p>}
              <div className={`flex items-center gap-4 mt-2 text-sm ${theme.textMuted}`}>
                <span className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  {email}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Full Name <span className="text-red-400">*</span>
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className={`w-full px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your full name"
                    required
                  />
                ) : (
                  <p className={theme.textPrimary}>{name}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Email
                </label>
                <p
                  className={`${theme.textPrimary} px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg`}
                >
                  {email}
                </p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Email cannot be changed</p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className={`w-full px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="+351 XXX XXX XXX"
                  />
                ) : (
                  <p className={theme.textPrimary}>{phone || 'Not set'}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Language <span className="text-red-400">*</span>
                </label>
                {isEditing ? (
                  <select
                    value={language}
                    onChange={e => handleLanguageChange(e.target.value)}
                    className={`w-full px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    required
                  >
                    <option value="pt">Português (PT)</option>
                    <option value="en">English (EN)</option>
                    <option value="es">Español (ES)</option>
                  </select>
                ) : (
                  <p className={theme.textPrimary}>
                    {language === 'pt'
                      ? 'Português (PT)'
                      : language === 'es'
                        ? 'Español (ES)'
                        : 'English (EN)'}
                  </p>
                )}
                {isEditing && language === 'es' && (
                  <p className="text-xs text-amber-400 mt-1">
                    Spanish translations are being completed. Some text may appear in English.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Timezone <span className="text-red-400">*</span>
                </label>
                {isEditing ? (
                  timezonesLoading ? (
                    <div
                      className={`w-full px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg ${theme.textSecondary} flex items-center gap-2`}
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                      Loading timezones...
                    </div>
                  ) : (
                    <select
                      value={timezone}
                      onChange={e => setTimezone(e.target.value)}
                      className={`w-full px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      required
                    >
                      <option value="">Select timezone...</option>
                      {timezones &&
                        Object.entries(timezones).map(([region, tzList]) => (
                          <optgroup key={region} label={region}>
                            {tzList.map(tz => (
                              <option key={tz.value} value={tz.value}>
                                {tz.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                    </select>
                  )
                ) : (
                  <p className={theme.textPrimary}>{timezone}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                  Account
                </label>
                <p
                  className={`${theme.textPrimary} px-3 py-2 ${theme.surface} border ${theme.border} rounded-lg`}
                >
                  {company || 'No account assigned'}
                </p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Managed by administrators</p>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className={`flex items-center gap-3 mt-6 pt-6 border-t ${theme.border}`}>
              <button
                onClick={handleSave}
                disabled={updateProfileMutation.isPending}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updateProfileMutation.isPending && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                )}
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={updateProfileMutation.isPending}
                className={`px-4 py-2 ${theme.surface} ${theme.textSecondary} rounded-lg hover:${theme.surfaceHover} transition disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
